import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { auth } from "@envirohub/utils";
import { apiClient } from "./client";
import { endpoints } from "./endpoints";
import { queryKeys } from "./keys";
import type {
  ApiEnvelope,
  LoginRequest,
  LoginResponse,
  RegisterRequest,
  RegisterResponse,
  RegisterOrganizationRequest,
  RegisterOrganizationResponse,
  VerifyOtpRequest,
  VerifyOtpResponse,
  SwitchContextRequest,
  SwitchContextResponse,
  ForgotPasswordRequest,
  ResetPasswordRequest,
  AuthUser,
  AcceptInvitationRequest,
  AcceptInvitationResponse,
  AuthContext,
} from "./types";

/** Auth mutations surface errors inline on the form, not via global toast. */
const inlineErrorMeta = { silentError: true } as const;

// ─── Service functions ─────────────────────────────────────────────
// Every endpoint returns the standard `{ success, message, data }` envelope;
// we unwrap `.data` so callers work with the domain payload directly.
async function login(payload: LoginRequest): Promise<LoginResponse> {
  const { data } = await apiClient.post<ApiEnvelope<LoginResponse>>(endpoints.auth.login, payload);
  return data.data;
}

async function register(payload: RegisterRequest): Promise<RegisterResponse> {
  const { data } = await apiClient.post<ApiEnvelope<RegisterResponse>>(
    endpoints.auth.register,
    payload,
  );
  return data.data;
}

async function registerOrganization(
  payload: RegisterOrganizationRequest,
): Promise<RegisterOrganizationResponse> {
  const { data } = await apiClient.post<ApiEnvelope<RegisterOrganizationResponse>>(
    endpoints.auth.registerOrganization,
    payload,
  );
  return data.data;
}

async function acceptInvitation(
  payload: AcceptInvitationRequest,
): Promise<AcceptInvitationResponse> {
  const { data } = await apiClient.post<ApiEnvelope<AcceptInvitationResponse>>(
    endpoints.auth.acceptInvitation,
    payload,
  );
  return data.data;
}

async function verifyOtp(payload: VerifyOtpRequest): Promise<VerifyOtpResponse> {
  const { data } = await apiClient.post<ApiEnvelope<VerifyOtpResponse>>(
    endpoints.auth.verifyEmail,
    payload,
  );
  return data.data;
}

async function resendOtp(email: string): Promise<void> {
  await apiClient.post(endpoints.auth.resendOtp, { email });
}

async function forgotPassword(payload: ForgotPasswordRequest): Promise<void> {
  await apiClient.post(endpoints.auth.forgotPassword, payload);
}

async function resetPassword(payload: ResetPasswordRequest): Promise<void> {
  await apiClient.post(endpoints.auth.resetPassword, payload);
}

async function switchContext(payload: SwitchContextRequest): Promise<SwitchContextResponse> {
  const { data } = await apiClient.post<ApiEnvelope<SwitchContextResponse>>(
    endpoints.auth.contextSwitch,
    payload,
  );
  return data.data;
}

async function getContexts(): Promise<AuthContext[]> {
  const { data } = await apiClient.get<ApiEnvelope<AuthContext[]>>(endpoints.auth.contexts);
  return data.data;
}

async function getMe(): Promise<AuthUser> {
  const { data } = await apiClient.get<ApiEnvelope<any>>(endpoints.auth.me);
  const me = data.data;
  return {
    id: me.id,
    email: me.email,
    firstName: me.profile?.firstName || "",
    lastName: me.profile?.lastName || "",
    phone: me.profile?.phone || "",
  };
}

// ─── React Query hooks ─────────────────────────────────────────────
export function useMe() {
  return useQuery({
    queryKey: ["auth", "me"],
    queryFn: getMe,
    staleTime: Infinity, // Shared state across the app
  });
}

/**
 * Contexts the user can switch to, fresh from the server so a company joined
 * since login shows up. Callers fall back to the session's list on error.
 */
export function useAuthContexts(enabled = true) {
  return useQuery({
    queryKey: queryKeys.auth.contexts,
    queryFn: getContexts,
    enabled,
    staleTime: 60_000,
    meta: inlineErrorMeta,
  });
}

export function useLogin() {
  return useMutation({
    mutationFn: login,
    meta: inlineErrorMeta,
  });
}

export function useSwitchContext() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: switchContext,
    meta: inlineErrorMeta,
    onSuccess: (data) => {
      auth.setAuth(data);
      queryClient.clear();
    },
  });
}

export function useRegister() {
  return useMutation({
    mutationFn: register,
    meta: inlineErrorMeta,
    onSuccess: (data) => {
      if (data.accessToken) auth.setToken(data.accessToken);
    },
  });
}

export function useRegisterOrganization() {
  return useMutation({
    mutationFn: registerOrganization,
    meta: inlineErrorMeta,
  });
}

export function useAcceptInvitation() {
  return useMutation({
    mutationFn: acceptInvitation,
    meta: inlineErrorMeta,
  });
}

export function useVerifyOtp() {
  return useMutation({
    mutationFn: verifyOtp,
    meta: inlineErrorMeta,
    onSuccess: (data) => {
      auth.setToken(data.accessToken);
    },
  });
}

export function useResendOtp() {
  return useMutation({ mutationFn: resendOtp, meta: inlineErrorMeta });
}

export function useForgotPassword() {
  return useMutation({ mutationFn: forgotPassword, meta: inlineErrorMeta });
}

export function useResetPassword() {
  return useMutation({ mutationFn: resetPassword, meta: inlineErrorMeta });
}

/** Best-effort server-side session revocation; local sign-out proceeds regardless. */
export async function logoutServerSession(): Promise<void> {
  try {
    await apiClient.post(endpoints.auth.logout);
  } catch {
    // Ignore network/API failures: the local session is cleared either way.
  }
}

export interface ChangePasswordPayload {
  currentPassword: string;
  newPassword: string;
}

async function changePassword(payload: ChangePasswordPayload): Promise<void> {
  await apiClient.post(endpoints.auth.changePassword, payload);
}

export function useChangePassword() {
  return useMutation({ mutationFn: changePassword });
}
